import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useQuery } from '@nerdwallet/apollo';
import nds from '@nerdwallet/base-styles/classes';
import VideoJsonLd, { QUERY } from '../../lib/videoJsonLd';

import { stringInputSpec, boolCheckboxSpec } from '../../lib/editing-configs';

import './video-embed.less';

const VideoEmbed = ({ privacyModeToggle, controlsToggle, videoSlug }) => {
  const { data, loading } = useQuery(QUERY, {
    variables: {
      youtubeVideoId: videoSlug,
    },
  });

  if (loading) {
    return null;
  }

  const youtubeVideo = data?.youtubeVideo;

  const privacyEnabled = 'youtube-nocookie';
  const privacyNotEnabled = 'youtube';
  const origin = '?origin=https://www.nerdwallet.com';
  const showControls = '';
  const hideControls = '&controls=0';
  const controls = controlsToggle ? hideControls : showControls;
  const youtubeDomain = privacyModeToggle ? privacyEnabled : privacyNotEnabled;
  const removeRelated = '&rel=0';
  const hiDef = '&hd=1';
  const videoUrl = `https://www.${youtubeDomain}.com/embed/${videoSlug}${origin}${controls}${removeRelated}${hiDef}`;

  return (
    <div
      className={classNames([
        'video-embed__cont',
        nds('position-relative'),
        nds('margin-vertical-5'),
        nds('margin-horizontal-auto'),
        nds('text-align-center'),
      ])}
    >
      <iframe
        title="video embed"
        id="player"
        className={classNames([
          'video-embed__iframe',
          nds('position-absolute'),
        ])}
        type="text/html"
        width="100%"
        height="100%"
        src={videoUrl}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
      {youtubeVideo && <VideoJsonLd {...data.youtubeVideo} />}
    </div>
  );
};

VideoEmbed.propTypes = {
  videoSlug: PropTypes.string,
  privacyModeToggle: PropTypes.bool,
  controlsToggle: PropTypes.bool,
};

VideoEmbed.defaultProps = {
  videoSlug: '',
  privacyModeToggle: false,
  controlsToggle: false,
};

VideoEmbed.getEditingConfig = () => [
  stringInputSpec('videoSlug', 'Video Slug', {
    help: 'The Video Slug is the string of text/numerals that immediately follows the https://youtu.be/ portion of the Share URL generated by YouTube.',
  }),
  boolCheckboxSpec('controlsToggle', 'Hide player controls?', {
    help: 'When checked, player controls will be hidden during playback',
  }),
  boolCheckboxSpec('privacyModeToggle', 'Enable privacy-enhanced mode?', {
    help: 'When checked, YouTube will not store information about visitors on your website unless they play the video',
  }),
];

VideoEmbed.displayName = 'VideoEmbed';

export default VideoEmbed;
